import { ChangeEvent, FocusEvent } from "react";
import { convertFileToSendToServer } from "../helpers/convertors";
import { object, string, mixed, ValidationError, number } from 'yup';
import {
    MAX_FILE_SIZE, FILE_TOO_LARGE_PATH, REQUIRED_FIELD_PATH,
    SUPPORTED_FORMATS, FILE_UNSUPPORTED_FORMAT_PATH
} from "../validations/errorMessages";
import { IFileDataBase64 } from "../interface/fileDataBase64.interface";
import {
    IUpdateVehicleFormValues,
    UpdateVehiclePermitFormErrorType,
    UpdateVehiclePermitFormValuesType, useGetUpdateVehiclePermitModalData,
    useSetAlertStateUpdateVehiclePermitModal,
    useSetModalUpdateVehiclePermitFormBlurAndError, useSetModalUpdateVehiclePermitFormError,
    useSetUpdateVehiclePermitValueForm,
    useVehiclePermitModalMoveToConfirmationStep
} from "../stores/updateVehiclePermitModal.store";
import useHomePageApi from "../axios/useHomePage.api";
import { useModalOtpData } from "../stores/otpModal.store";
import OwnershipCarType from "../types/ownershipCar.type";
import useFilesApi from "../axios/useFiles.api";
import { useGetUseNewApiApp } from "../stores/app.store";
import convertBase64ToBlob from "../helpers/files/convert-base64-to-blob";

const userSchema = object().shape({
    newVehicleNum: string()
        .required(REQUIRED_FIELD_PATH)
        .matches(/^[0-9]+$/, 'מספר הרכב חייב להכיל רק מספרים'),
    newColorId: number().
        notOneOf([-1], 'נא לבחור צבע רכב'),
    newTypeId: number().
        notOneOf([-1], 'נא לבחור סוג רכב'),
    newVehicleGroupId: number().
        notOneOf([-1], 'נא לבחור קבוצת רכב'),
    citizenComment: string()
        .max(500, 'ההערה יכולה להכיל עד 500 תווים בלבד'),
    scanCarId: mixed()
        .nonNullable()
        .required(REQUIRED_FIELD_PATH)
        .test(
            "FILE_SIZE",
            FILE_TOO_LARGE_PATH,
            (value: IFileDataBase64) => {

                return !value || (value && value.size <= MAX_FILE_SIZE)
            }
        ).test(
            "FILE_FORMAT",
            FILE_UNSUPPORTED_FORMAT_PATH,
            (value: IFileDataBase64) => {

                return !value || (value && SUPPORTED_FORMATS.includes(value?.contentType))
            }
        ),
});

const useUpdateVehicleModalFunctions = () => {

    const modalOtpData = useModalOtpData();
    const { updateVehicleOtpApi } = useHomePageApi();
    const { uploadFilesApi } = useFilesApi();
    const useNewApi = useGetUseNewApiApp();
    const setModalUpdateVehiclePermitFormError = useSetModalUpdateVehiclePermitFormError();
    const moveToConfirmationStep = useVehiclePermitModalMoveToConfirmationStep();

    const setBlurAndError = useSetModalUpdateVehiclePermitFormBlurAndError();
    const setValueForm = useSetUpdateVehiclePermitValueForm();
    const setAlert = useSetAlertStateUpdateVehiclePermitModal()
    const updateVehicleModalData = useGetUpdateVehiclePermitModalData();
    const { form, personId, otpCode, vehicleNum } = updateVehicleModalData;


    async function handleBlur(event: FocusEvent<HTMLInputElement> | FocusEvent<HTMLTextAreaElement>) {

        const field = event.target.name as UpdateVehiclePermitFormValuesType;

        console.log('handleBlur called for field:', field);
        const [isValid, errors, lastError] = await validateAllFormFields();

        setBlurAndError(field, errors);
    }

    async function handleOnChangeFile(event: ChangeEvent<HTMLInputElement>) {

        const file = event.currentTarget.files[0];
        const name = event.currentTarget.name as UpdateVehiclePermitFormValuesType;

        console.log(name)
        const fileDataBase64 = await convertFileToSendToServer(file, event.currentTarget.name);

        setValueForm(name, fileDataBase64);
    }

    function handleOnChange(event:
        ChangeEvent<HTMLInputElement> |
        ChangeEvent<HTMLTextAreaElement> |
        ChangeEvent<HTMLSelectElement>
    ) {

        const field = event.currentTarget.name as UpdateVehiclePermitFormValuesType;
        const value = event.currentTarget.value;

        setValueForm(field, value);
    }

    async function submitUpdateVehicleForm() {

        setAlert('loading');

        const [isValid, , lastError] = await validateAllFormFields(true);

        if (!isValid) {

            setAlert('hasAlert', lastError.message, 'danger');
            return;
        }

        const res = await handlePreSendFiles({ ...form.values }, personId);

        if (!res.isSuccess) {
            setAlert('hasAlert', res.message, 'danger');
            return;
        }

        const requestWithFilesNames = res.bodyToSend;

        const isPermit = true;

        const formToSend = {
            ...requestWithFilesNames,
            isCompany: requestWithFilesNames.ownershipCar === OwnershipCarType.COMPANY,
            personId,
            vehicleNum,
            otpCode,
            isPermit,
            carIdFileName: requestWithFilesNames.scanCarId.name,
            workStatementFileName: requestWithFilesNames.scanWorkStatment.name
        }

        const { isSuccess, message, requestId } = await updateVehicleOtpApi(formToSend);

        if (!isSuccess) {

            setAlert('hasAlert', message, 'danger');
            return;
        }

        moveToConfirmationStep(requestId);
    }

    async function handlePreSendFiles(bodyToSend: IUpdateVehicleFormValues, personId: string): Promise<{
        isSuccess: boolean,
        message: string,
        bodyToSend: IUpdateVehicleFormValues
    }> {

        const result = {
            isSuccess: true,
            message: '',
            bodyToSend: bodyToSend
        }

        if (!useNewApi) {
            return result;
        }

        const modifiedBody = { ...bodyToSend };

        // Convert and upload files first
        const fileFields = [] as { name: string, file: Blob }[];
        const allFileDataBase64s: IFileDataBase64[] = [
            bodyToSend.scanCarId,
            bodyToSend.scanWorkStatment,
        ];

        allFileDataBase64s.forEach((fileDataBase64) => {

            if (fileDataBase64?.content && fileDataBase64.size > 50) {

                console.log(`Preparing file for upload: ${fileDataBase64.name}, size: ${fileDataBase64.size}, contentType: ${fileDataBase64.contentType}`);
                const blob = convertBase64ToBlob(fileDataBase64.content, fileDataBase64.contentType);
                fileFields.push({ name: fileDataBase64.name, file: blob });
            }
        });


        console.log('Request to upload files:', fileFields);

        const responseUploadFiles = await uploadFilesApi(personId, fileFields);

        if (!responseUploadFiles.isSuccess) {

            return {
                isSuccess: false,
                message: responseUploadFiles.message,
                bodyToSend: bodyToSend
            };
        }

        responseUploadFiles.list.forEach(fileResponse => {

            if (fileResponse.originalFileName === 'scanCarId') {
                modifiedBody.scanCarId.name = fileResponse.fileName;
                modifiedBody.scanCarId.content = '';
            }

            if (fileResponse.originalFileName === 'scanWorkStatment') {
                modifiedBody.scanWorkStatment.name = fileResponse.fileName;
                modifiedBody.scanWorkStatment.content = '';
            }

        });

        return { isSuccess: true, message: 'קובץ עולה בהצלחה', bodyToSend: modifiedBody };
    }

    async function validateAllFormFields(markFieldsAsTouched: boolean = false):
        Promise<[boolean, UpdateVehiclePermitFormErrorType, { field: string, message: string }]> {

        const errors: UpdateVehiclePermitFormErrorType = {};
        const values = form.values;
        let lastError = { field: '', message: '' };

        let isValid = false;

        try {

            await userSchema.validate(values, { abortEarly: false });
            isValid = true;
        } catch (err) {

            if (err instanceof ValidationError) {
                err.inner.forEach((error) => {
                    const field = error.path as UpdateVehiclePermitFormValuesType;
                    errors[field] = error.message;
                    lastError = { field, message: error.message };
                }
                );
            }
        }

        setModalUpdateVehiclePermitFormError(errors);

        if (markFieldsAsTouched) {
            Object.keys(errors).forEach((field) => {
                setBlurAndError(field as UpdateVehiclePermitFormValuesType, errors);
            });
        }

        console.log('Validation Errors:', errors);
        return [isValid, errors, lastError];
    }

    return {
        submitUpdateVehicleForm,
        handleOnChangeFile,
        handleOnChange,
        handleBlur,
    };
}

export default useUpdateVehicleModalFunctions;